import React, { useState, useMemo } from 'react';
import Header from 'core/layout/Header';
import MinimalFooter from 'features/feature_home/components/MinimalFooter';
import CandidatesFilterSidebar from '../components/CandidatesFilterSidebar';
import CandidatesList from '../components/CandidatesList';
import {
    Box,
    Container,
    Grid,
    TextField,
    InputAdornment,
    Button,
    CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchHero from 'core/components/SearchHero';
import { Search as SearchLucide, MapPin } from 'lucide-react';
import { useTranslation } from 'core/context/TranslationContext';


function CandidatesPage() {
    const [titleInput, setTitleInput] = useState('');
    const [locationInput, setLocationInput] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);

    const [sidebarFilters, setSidebarFilters] = useState({
        country: '', city: '', skill: '', experience_level: '', title: ''
    });
    const [appliedSidebarFilters, setAppliedSidebarFilters] = useState({});

    const [quickFilters, setQuickFilters] = useState({});

    const { _t } = useTranslation();

    const sanitize = (obj) =>
        Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== '' && v !== null && v !== undefined));

    const mergedFilters = useMemo(
        () => sanitize({ ...appliedSidebarFilters, ...quickFilters }),
        [appliedSidebarFilters, quickFilters]
    );

    const handleQuickSearch = (e) => {
        e.preventDefault();
        const q = {};
        if (titleInput.trim()) q.title = titleInput.trim();
        if (locationInput.trim()) q.city = locationInput.trim();
        setQuickFilters(q);
    };

    const handleApplySidebar = (newFilters) => setAppliedSidebarFilters(newFilters);

    const handleResetSidebar = () => {
        setSidebarFilters({ country: '', city: '', skill: '', experience_level: '', title: '' });
        setAppliedSidebarFilters({});
    };

    const handleRemoveSidebarFilter = (key) => {
        setAppliedSidebarFilters(prev => { const n = { ...prev }; delete n[key]; return n; });
        setSidebarFilters(prev => ({ ...prev, [key]: '' }));
    };

    return (
        <>
            <Header />
            <Box
                sx={{
                    minHeight: '100vh',
                    bgcolor: 'grey.50',
                    display: 'flex',
                    flexDirection: 'column',
                    pt: 0
                }}
            >
                {/* Search hero */}
                <SearchHero
                    compact
                    title={_t('Discover talent')}
                    lead={_t('Browse candidate profiles by title, skills and location.')}
                    onSubmit={handleQuickSearch}
                    loading={isSearching}
                    submitLabel={_t('Search')}
                    fields={[
                        { key: 'title', placeholder: _t('Job title or keyword'), icon: SearchLucide, value: titleInput, onChange: setTitleInput },
                        { key: 'loc', placeholder: _t('City'), icon: MapPin, value: locationInput, onChange: setLocationInput }
                    ]}
                />

                {/* Main content */}
                <Container maxWidth="xl" sx={{ pt: 2, pb: 3, flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <Button
                            variant={filtersOpen ? 'contained' : 'outlined'}
                            size="medium"
                            startIcon={<FilterListIcon />}
                            onClick={() => setFiltersOpen((o) => !o)}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            {filtersOpen ? 'Hide filters' : 'Filters'}
                        </Button>
                    </Box>
                    <Grid container spacing={2} alignItems="flex-start">
                        {filtersOpen && (
                            <Grid item xs={12} md={3}>
                                <Box sx={{ position: 'sticky', top: { xs: 68, md: 80 } }}>
                                    <CandidatesFilterSidebar
                                        filters={sidebarFilters}
                                        onFilterChange={(name, value) =>
                                            setSidebarFilters(prev => ({ ...prev, [name]: value }))
                                        }
                                        onApplyFilters={handleApplySidebar}
                                        onResetFilters={handleResetSidebar}
                                        appliedFilters={appliedSidebarFilters}
                                        onRemoveFilter={handleRemoveSidebarFilter}
                                    />
                                </Box>
                            </Grid>
                        )}
                        <Grid item xs={12} md={filtersOpen ? 9 : 12}>
                            <CandidatesList
                                filters={mergedFilters}
                                onLoadingChange={setIsSearching}
                            />
                        </Grid>
                    </Grid>
                </Container>
            </Box>
            <MinimalFooter />
        </>
    );
}

export default CandidatesPage;
